/**
 * A festival that has to be bought (plan.md §54, Phase 4).
 *
 * Shown when the bundle answers **402**, which carries the price with it —
 * that is why the API refuses with 402 and not 403. A paywall rendered as a
 * permission error is a paywall nobody buys through: "not permitted" is a dead
 * end, "€12 to open this" is a decision.
 *
 * **The client never names a price.** It displays what the refusal said and
 * posts an empty purchase; the amount is the edition's, read server-side. A
 * client that could name a price is a client that will.
 *
 * **A card payment is three steps and the middle one is Stripe's.** The API
 * answers a purchase with a PENDING payment and a `clientSecret` for a
 * Checkout Session; Stripe.js (loaded from js.stripe.com, see `stripe.ts`)
 * mounts its EMBEDDED Checkout, which shows the price in the buyer's own
 * currency and takes the payment (the user, 2026-10-07: prices in USD, the
 * buyer pays in their local currency — Stripe Adaptive Pricing, which needs a
 * Checkout Session); then Stripe tells the API by a signed webhook. The
 * browser's "complete" is NOT access — only the webhook grants it — so the
 * last step polls `GET /editions/:id/access` until it says so, and says
 * plainly when that is taking longer than it should.
 *
 * **A code opens it for less, or for nothing** (the user, 2026-10-07:
 * coupons replace comps). The code goes with the purchase; a free one
 * answers SUCCEEDED at once — no card, no Stripe — and a discount opens the
 * card form at the lower price.
 *
 * The free provider still answers SUCCEEDED at once, and that path is
 * unchanged: no script, no card, no polling.
 *
 * **MyRave sets the price** (the user, 2026-10-07, evening): the refusal
 * carries the one-rave price ($5), never the festival's. Two choices (the
 * user, 2026-10-08): buy this rave — or, holding a pack credit or a tier rave,
 * use one (`RaveOptions.tsx` — only when pressed) — or "See deals and plans"
 * on `app.…/plans`, where the pack and the plans are. **In the phone app** the
 * purchase goes through the App Store / Google Play instead (`lib/store.ts`):
 * no Stripe form and no code there — the stores' rules.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AccessDecision, type PaymentRow, type PurchaseResponse } from '@/lib/api';
import { useAuth } from '@/app/AuthProvider';
import { Badge, Button, Card, Stack, Empty, Spinner, Txt, localDateRange, localCountry } from '@/ui';
import { useT, type Vars } from '@/i18n';
import { loadStripe, type StripeEmbeddedCheckout } from './stripe';
import { buysInStore } from '@/lib/store';
import { RaveStrip, SeePlansLink, SpendOptions, StoreRaveButton, useHasSpendable } from './RaveOptions';
import { useTenant } from '@/app/TenantProvider';
import { formatDateRange } from '@myrave/shared';
import './Paywall.css';
import type { AnyProductId } from '@myrave/shared';
import { useLocale } from '@/app/LocaleProvider';
import { usePurchasesOn } from '@/lib/catalogue';

type Tr = (english: string, vars?: Vars) => string;

export interface PaywallDetails {
  editionId: string;
  priceCents?: number | null;
  currency?: string | null;
  /**
   * The product that sells this rave — `RAVE_SINGLE` on a MyRave rave, an
   * Independent Rave's own price step (`RAVE_PRICE_1500`). Older servers
   * sent none: the one-rave price.
   */
  product?: AnyProductId | null;
}

/**
 * Money for a human, from minor units. Never a float. In the CHOSEN language,
 * never the browser's (step 73) — and `en`, not `en-GB`: "$5.00", not "US$5.00".
 */
function price(cents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    // An unknown currency code must not throw in the middle of a price.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/*
 * The two sentences that are always true after a failure. Functions of `tr`,
 * not constants: a module-level string is fixed in English before anybody has
 * chosen a language.
 */
const notCharged = (tr: Tr) => tr('That did not go through. Nothing has been charged.');
const declined = (tr: Tr) => tr('The payment did not go through. Nothing has been charged.');
/** The provider's own reason, then the promise that matters. */
const withReason = (tr: Tr, reason: string) => tr('{reason} Nothing has been charged.', { reason });
/*
 * After the card was confirmed, a FAILED row is NOT proof that nothing was
 * charged: the row closes when an intent is cancelled, and the same intent may
 * still have been paid — the server then settles it and the rave opens. So
 * this says what is certain and promises nothing about the money (review B1).
 */
const declinedAfterConfirm = (tr: Tr) => tr('The card was declined. You can try again.');

type Phase =
  | { step: 'idle' }
  | { step: 'card'; paymentId: string; clientSecret: string; publishableKey: string }
  | { step: 'confirming'; paymentId: string | null }
  | { step: 'slow'; paymentId: string | null };

/** The reason a refusal carried, or the sentence that is always true. */
function failureMessage(e: unknown, tr: Tr): string {
  if (!(e instanceof ApiError)) return notCharged(tr);
  const body = e.body as { payment?: { status?: string; failureReason?: string }; message?: string } | undefined;
  // A FAILED purchase is a 402 whose body is the payment, carrying the
  // provider's reason. With none, say what is certain.
  if (body?.payment?.status === 'FAILED' && !body.message) {
    return body.payment.failureReason ? withReason(tr, body.payment.failureReason) : declined(tr);
  }
  return e.message;
}

export function Paywall({
  festivalName, details, onPurchased, pollEveryMs = 2000, giveUpAfterMs = 60_000,
}: {
  festivalName: string;
  details: PaywallDetails;
  onPurchased: () => void;
  /** How often to ask whether the webhook has landed. */
  pollEveryMs?: number;
  /** When to stop asking and say it is taking a while. */
  giveUpAfterMs?: number;
}) {
  const { user } = useAuth();
  const tr = useT();
  const { locale } = useLocale();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ step: 'idle' });
  const queryClient = useQueryClient();
  /*
   * Access granted — by the purchase itself or by the webhook the wait was
   * polling for. The server marked them attending when it granted it
   * (`billing/service.ts` settlePayment), so "My festivals" is stale from
   * this moment: dropped here, whoever renders the paywall, rather than
   * waiting out its cache.
   */
  const granted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'festivals'] });
    onPurchased();
  }, [queryClient, onPurchased]);

  /*
   * Back from a payment method that had to leave the page (a bank's own
   * screen). Stripe returns to the path we gave it, marked `?checkout=return`;
   * the webhook decides the outcome, so this only resumes the wait — and
   * strips the mark, which is nobody's business in a bookmark.
   */
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('checkout') !== 'return') return;
    url.searchParams.delete('checkout');
    window.history.replaceState(window.history.state, '', url.toString());
    setPhase({ step: 'confirming', paymentId: null });
    // Once, on arrival: the query string is read and then removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSpendable = useHasSpendable(Boolean(user));
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState('');

  const buy = useMutation({
    // No amount in the body, deliberately — see the header. The path to come
    // back to (only a path: the API puts it on our own origin), and a code.
    mutationFn: (coupon?: string) => {
      const back = new URL(window.location.href);
      back.searchParams.set('checkout', 'return');
      return api.post<PurchaseResponse>(`/editions/${details.editionId}/purchase`, {
        returnPath: `${back.pathname}${back.search}`,
        ...(coupon ? { coupon } : {}),
      });
    },
    onSuccess: (res) => {
      const payment = res?.payment;
      if (!payment || payment.status === 'SUCCEEDED' || payment.status === 'ALREADY_GRANTED' || res.access?.granted) {
        granted();
        return;
      }
      if (payment.status === 'FAILED') {
        setError(payment.failureReason ? withReason(tr, payment.failureReason) : declined(tr));
        return;
      }
      // PENDING from here on.
      if (payment.clientSecret && res.publishableKey) {
        setPhase({
          step: 'card', paymentId: payment.id,
          clientSecret: payment.clientSecret, publishableKey: res.publishableKey,
        });
        return;
      }
      if (payment.redirectUrl) { window.location.assign(payment.redirectUrl); return; }
      // Pending with nothing for the payer to do: the provider is deciding.
      setPhase({ step: 'confirming', paymentId: payment.id });
    },
    onError: (e: Error) => setError(failureMessage(e, tr)),
  });

  if (!user) {
    return (
      <Empty
        title={tr('{festival} needs an account', { festival: festivalName })}
        hint={tr('Sign in to open this rave.')}
      />
    );
  }

  const amount =
    details.priceCents && details.currency
      ? price(details.priceCents, details.currency, locale)
      : null;

  const waiting = phase.step === 'confirming' || phase.step === 'slow';
  const inStore = buysInStore();
  // Holding a pack credit or a plan rave makes "use it" the primary action,
  // and buying the secondary one (the user, 2026-10-08).
  const buyPrimary = !hasSpendable;

  return (
    <Stack gap={14} className="paywall-page">
      <PaywallHero festivalName={festivalName} />

      {error && (
        <Card className="paywall-error-card">
          <Txt as="div" role="alert" size={12.5} tone="bad">{error}</Txt>
        </Card>
      )}

      {/*
        * Two choices, and only two (the user, 2026-10-08: "buy this rave
        * for $5, or browse the buying deals and subscriptions"): open THIS
        * rave — with what the account already holds, else for its price —
        * or go and see the pack and the plans on their own page.
        */}
      {phase.step === 'idle' && (
        <Stack gap={10} className="paywall-actions">
          <SpendOptions editionId={details.editionId} onGranted={granted} />
          {inStore && (
            <StoreRaveButton
              editionId={details.editionId} primary={buyPrimary} onGranted={granted}
              product={details.product ?? 'RAVE_SINGLE'} priceCents={details.priceCents ?? null}
            />
          )}
          {!inStore && (
            <Button variant={buyPrimary ? 'primary' : 'secondary'} full disabled={buy.isPending} onClick={() => { setError(null); buy.mutate(undefined); }}>
              {buy.isPending
                ? tr('One moment…')
                : !amount ? tr('Open this rave')
                  : buyPrimary ? tr('Buy this rave — {price}', { price: amount }) : tr('Buy for {price}', { price: amount })}
            </Button>
          )}
          <SeePlansLink editionId={details.editionId} />
        </Stack>
      )}

      {phase.step === 'idle' && !inStore && !codeOpen && (
        <button type="button" className="paywall-code-toggle" onClick={() => setCodeOpen(true)}>{tr('I have a code')}</button>
      )}
      {phase.step === 'idle' && !inStore && codeOpen && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (code.trim()) { setError(null); buy.mutate(code.trim()); } }}
        >
          <Stack gap={8}>
            <label htmlFor="paywall-code" className="paywall-code-label">{tr('Your code')}</label>
            <input
              id="paywall-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              autoComplete="off" autoCapitalize="characters" dir="ltr" className="ui-input"
            />
            <Button type="submit" variant="secondary" full disabled={!code.trim() || buy.isPending}>
              {tr('Use the code')}
            </Button>
          </Stack>
        </form>
      )}

      {phase.step === 'card' && (
        <CardForm
          clientSecret={phase.clientSecret}
          publishableKey={phase.publishableKey}
          onConfirmed={() => { setError(null); setPhase({ step: 'confirming', paymentId: phase.paymentId }); }}
          onCancel={() => setPhase({ step: 'idle' })}
        />
      )}

      {waiting && (
        <Confirming
          editionId={details.editionId}
          paymentId={phase.paymentId}
          slow={phase.step === 'slow'}
          pollEveryMs={pollEveryMs}
          giveUpAfterMs={giveUpAfterMs}
          onGranted={granted}
          onFailed={() => { setPhase({ step: 'idle' }); setError(declinedAfterConfirm(tr)); }}
          onSlow={() => setPhase({ step: 'slow', paymentId: phase.paymentId })}
          onRetry={() => setPhase({ step: 'confirming', paymentId: phase.paymentId })}
        />
      )}

      {!waiting && (
        <Txt as="p" size={11} tone="faint" lh={1.45} className="paywall-fine">
          {tr('You are charged once for this edition, not a subscription.')}
        </Txt>
      )}
    </Stack>
  );
}

/**
 * What a paid rave's 402 shows — the paywall only while payments are ON
 * (`usePurchasesOn`, the one answer: `/billing/catalogue`'s `purchasesOn`).
 * While they are off — `PURCHASES_DISABLED`, or the deployment's
 * `PAYMENTS_PAUSED` (the user, 2026-10-29: "all the payment stuff on all the
 * pages should be hidden") — the rave, and that it is not open right now: no
 * price, no buy, no code, no plans. Until the API has said, the rave alone —
 * never a paywall on a guess.
 */
export function PaidRave(props: Parameters<typeof Paywall>[0]) {
  const on = usePurchasesOn();
  if (on === true) return <Paywall {...props} />;
  return (
    <Stack gap={14} className="paywall-page" data-testid="rave-not-open">
      <PaywallHero festivalName={props.festivalName} closed={on === false ? 'said' : 'quiet'} />
    </Stack>
  );
}

/**
 * The rave itself, first: its cover behind, its logo, its name, its dates and
 * place, and one line of what opening it gives. All of it from `/tenant`,
 * which a 402 does not withhold — the bundle is what is refused, not the rave.
 * `closed`: payments are off (no badge, no offer) — `said` says so, `quiet`
 * (not answered yet) says nothing.
 */
function PaywallHero({ festivalName, closed }: { festivalName: string; closed?: 'said' | 'quiet' }) {
  const tr = useT();
  const { locale } = useLocale();
  const { tenant } = useTenant();
  const edition = tenant?.editions?.find((e) => e.id === tenant.editionId);
  const dates = edition?.gatesOpenAt && edition.endsAt
    ? localDateRange(locale, formatDateRange(new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone), new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone)
    : null;
  // The country by NAME in the reader's language, not its code ("HU").
  const place = [edition?.venue ?? tenant?.venue, localCountry(locale, tenant?.country)].filter(Boolean).join(' · ');

  return (
    <RaveStrip
      name={festivalName}
      detail={[dates, place].filter(Boolean).join(' · ') || null}
      logoUrl={edition?.logoUrl ?? tenant?.logoUrl}
      coverUrl={tenant?.coverUrl}
      eyebrow={closed ? undefined : <span><Badge tone="warn">{tr('Paid rave')}</Badge></span>}
    >
      {closed === 'said' ? (
        <Txt as="p" size={13} lh={1.5}>{tr('This rave is not open right now. Check back soon.')}</Txt>
      ) : !closed && (
        <Txt as="p" size={13} lh={1.5}>
          {tr('This rave is not included yet. Opening it gives you the full lineup, the map and everything offline.')}
        </Txt>
      )}
    </RaveStrip>
  );
}

/**
 * Stripe's embedded Checkout, mounted into a div we own. It draws the price
 * in the buyer's own currency, the payment methods and its own pay button;
 * a payment that finishes in place calls `onComplete`, and one that must
 * visit its bank comes back to `?checkout=return` (the effect in `Paywall`).
 */
export function CardForm({
  clientSecret, publishableKey, onConfirmed, onCancel,
}: {
  clientSecret: string;
  publishableKey: string;
  onConfirmed: () => void;
  onCancel: () => void;
}) {
  const tr = useT();
  const host = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'broken'>('loading');
  const [error, setError] = useState<string | null>(null);
  // The latest handler, so a re-render does not remount a form mid-payment.
  const done = useRef(onConfirmed);
  done.current = onConfirmed;

  useEffect(() => {
    let cancelled = false;
    let checkout: StripeEmbeddedCheckout | null = null;
    loadStripe()
      .then(async (Stripe) => {
        if (cancelled || !host.current) return;
        const made = await Stripe(publishableKey).initEmbeddedCheckout({
          fetchClientSecret: () => Promise.resolve(clientSecret),
          onComplete: () => done.current(),
        });
        if (cancelled || !host.current) { made.destroy(); return; }
        checkout = made;
        made.mount(host.current);
        setState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        // `stripe.ts` rejects for one reason only — the script did not load —
        // and says it in English, outside React; the reader's sentence is here.
        setError(tr('The card form could not be loaded. Check your connection and try again.'));
        setState('broken');
      });
    return () => { cancelled = true; checkout?.destroy(); };
    // `tr` changing is not a reason to remount a card form mid-payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, publishableKey]);

  return (
    <Stack gap={12}>
      <div ref={host} data-testid="stripe-checkout" />
      {state === 'loading' && <Spinner label={tr('Loading the card form…')} />}
      {error && <Txt as="div" role="alert" size={12.5} tone="bad">{error}</Txt>}
      <Button variant="quiet" full onClick={onCancel}>{tr('Not now')}</Button>
    </Stack>
  );
}

/**
 * The card is confirmed; the access is not yet.
 *
 * Stripe settles by webhook, usually within seconds. Until the API says the
 * edition is open, this asks again — and also watches the payment row, so a
 * charge the bank refused after all is reported rather than waited on forever.
 */
function Confirming({
  editionId, paymentId, slow, pollEveryMs, giveUpAfterMs, onGranted, onFailed, onSlow, onRetry,
}: {
  editionId: string;
  paymentId: string | null;
  slow: boolean;
  pollEveryMs: number;
  giveUpAfterMs: number;
  onGranted: () => void;
  onFailed: () => void;
  onSlow: () => void;
  onRetry: () => void;
}) {
  // The parent passes inline arrows; a ref keeps the clock from restarting on
  // every render.
  const tr = useT();
  const handlers = useRef({ onGranted, onFailed, onSlow });
  handlers.current = { onGranted, onFailed, onSlow };

  const check = useCallback(async (): Promise<'granted' | 'failed' | 'waiting'> => {
    try {
      const access = await api.get<AccessDecision>(`/editions/${editionId}/access`);
      if (access.granted) return 'granted';
      if (paymentId) {
        const { payments } = await api.get<{ payments: PaymentRow[] }>('/me/payments');
        if (payments.find((p) => p.id === paymentId)?.status === 'FAILED') return 'failed';
      }
    } catch {
      // A blip is not an answer. Keep asking until the deadline.
    }
    return 'waiting';
  }, [editionId, paymentId]);

  useEffect(() => {
    if (slow) return;
    let stopped = false;
    const started = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      const outcome = await check();
      if (stopped) return;
      if (outcome === 'granted') { handlers.current.onGranted(); return; }
      if (outcome === 'failed') { handlers.current.onFailed(); return; }
      if (Date.now() - started >= giveUpAfterMs) { handlers.current.onSlow(); return; }
      timer = setTimeout(() => void tick(), pollEveryMs);
    };
    void tick();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [slow, check, pollEveryMs, giveUpAfterMs]);

  if (slow) {
    return (
      <Card>
        <Stack gap={10} className="paywall-slow">
          <Txt as="div" role="status" size={13} lh={1.55}>
            {tr('This is taking longer than usual. If your payment went through, the rave opens by itself — checking again will not charge you twice.')}
          </Txt>
          <Button variant="secondary" onClick={onRetry}>{tr('Check again')}</Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Spinner label={tr('Confirming your payment… this rave opens as soon as your bank says yes.')} />
    </Card>
  );
}
